import { PageSize } from '@/constant';
import {
	GetWorkerApplicantListsDetailAssignmentParams,
	GetWorkerApplicantListsDetailParams,
	GetWorkerApplicantListsDetailProcessInfoParams,
	GetWorkerApplicantListsDetailProgramParams,
	IApplicantListItem,
	IApplicantNameplate,
	TApplicantAssignmentItem,
	TApplicantItem,
	TApplicantLodging,
	TApplicantLodgingAutoFill,
	TApplicantRecommender,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';

import { omit } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import {
	formatWorkerApplicantDetail,
	formatWorkerApplicantList,
	RootState,
} from '..';
import { storage } from '../store.persist';
import {
	IPayloadApplicantCardRequest,
	IPayloadCreateWorkerApplicantLodgingRequest,
	IPayloadCreateWorkerApplicantProgramRequest,
	IPayloadCreateWorkerApplicantRequest,
	IPayloadDeleteWorkerApplicantProcessInfoRequest,
	IPayloadDeleteWorkerApplicantRequest,
	IPayloadGetPublicFranchiseIdSuccess,
	IPayloadGetWorkerApplicantAssignmentListRequest,
	IPayloadGetWorkerApplicantAssignmentListSuccess,
	IPayloadGetWorkerApplicantDepartureDateRequest,
	IPayloadGetWorkerApplicantDetailRequest,
	IPayloadGetWorkerApplicantDetailSuccess,
	IPayloadGetWorkerApplicantExcelListRequest,
	IPayloadGetWorkerApplicantListRequest,
	IPayloadGetWorkerApplicantListSuccess,
	IPayloadGetWorkerApplicantLodgingAutoFillSuccess,
	IPayloadGetWorkerApplicantLodgingSuccess,
	IPayloadGetWorkerApplicantProcessInfoListRequest,
	IPayloadGetWorkerApplicantProcessInfoListSuccess,
	IPayloadGetWorkerApplicantProgramListRequest,
	IPayloadGetWorkerApplicantProgramListSuccess,
	IPayloadGetWorkerApplicantRecommenderSuccess,
	IPayloadUpdateExtensionPeriodRequest,
	IPayloadUpdateStatusWorkerApplicantRequest,
	IPayloadUpdateWorkerApplicantRecommenderRequest,
	IWorkerApplicantDetailStore,
	IWorkerApplicantListStore,
	IWorkerApplicantLodgingStore,
	IWorkerApplicantProcessInfoListStore,
	IWorkerApplicantProgramListStore,
	IWorkerApplicantRecommenderStore,
	TApplicantUploadFailedDetailStore,
	TPayloadUploadExcelApplicantRequest,
	TPayloadUploadExcelApplicantSuccess,
} from './worker-applicant.type';

const InitParams: IPayloadGetWorkerApplicantListRequest = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const InitListsDetailParams: GetWorkerApplicantListsDetailParams = {
	page: 1,
	pageSize: PageSize.S,
	id: 0,
};

const InitListsDetailAssignmentParams: GetWorkerApplicantListsDetailAssignmentParams =
	{
		page: 1,
		pageSize: PageSize.S,
		id: 0,
	};

const InitListsDetailProgramParams: GetWorkerApplicantListsDetailProgramParams =
	{
		page: 1,
		pageSize: PageSize.S,
		id: 0,
	};
const InitListsDetailProcessInfoParams: GetWorkerApplicantListsDetailProcessInfoParams =
	{
		page: 1,
		pageSize: PageSize.S,
		id: 0,
	};

const initialState = {
	fetching: false,
	excelListFetching: false,
	total: 0,
	totalAssignment: 0,
	totalProgram: 0,
	totalProcessInfo: 0,
	params: InitParams,
	paramsListsDetail: InitListsDetailParams,
	paramsListsDetailAssignment: InitListsDetailAssignmentParams,
	paramsListsDetailProgram: InitListsDetailProgramParams,
	paramsListsDetailProcessInfo: InitListsDetailProcessInfoParams,
	publicFranchiseId: null as number | null,
	workerApplicantList: [] as IWorkerApplicantListStore[],
	workerApplicantDetail: {} as IWorkerApplicantDetailStore,
	workerApplicantAssignmentFetching: false,
	workerApplicantAssignmentList: [] as TApplicantAssignmentItem[],
	workerApplicantProgramList: [] as IWorkerApplicantProgramListStore[],
	workerApplicantRecommender: [] as IWorkerApplicantRecommenderStore[],
	workerApplicantLodging: [] as IWorkerApplicantLodgingStore[],
	workerApplicantProcessInfoList: [] as IWorkerApplicantProcessInfoListStore[],
	applicantUploadFailedList: [] as TApplicantUploadFailedDetailStore[],
	workerApplicantLodgingAutoFill: {} as TApplicantLodgingAutoFill,
	nameplate: {} as IApplicantNameplate,
	nameplateFetching: false,
	uploadExcelResult: {
		successCount: 0,
		failCount: 0,
	} as {
		successCount: number;
		failCount: number;
	},
};

const workerApplicantSlice = createSlice({
	initialState,
	name: 'workerApplicant',
	reducers: {
		/**---- GET WORKER_APPLICANT LIST START ---- */
		getWorkerApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getWorkerApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerApplicantList = content;
			state.total = total;
		},
		getWorkerApplicantListFailure: (state) => {
			state.fetching = false;
		},
		resetParamsWorkerApplicantListDashboard: (state) => {
			const initParams = { ...state.params };
			state.params = {
				...omit(['fullName', 'status'], initParams),
			};
		},

		getWorkerApplicantExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerApplicantExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerApplicantExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER_APPLICANT LIST END ---- */

		/**---- GET APPLICANT ASSIGNMENT LIST START ---- */
		getWorkerApplicantAssignmentListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantAssignmentListRequest>
		) => {
			state.workerApplicantAssignmentFetching = true;
			state.paramsListsDetailAssignment = action?.payload;
		},
		getWorkerApplicantAssignmentListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantAssignmentListSuccess>
		) => {
			const { content, total } = action.payload;
			state.workerApplicantAssignmentFetching = false;
			state.workerApplicantAssignmentList = content;
			state.totalAssignment = total;
		},
		getWorkerApplicantAssignmentListFailure: (state) => {
			state.workerApplicantAssignmentFetching = false;
		},
		/**---- GET APPLICANT ASSIGNMENT LIST END ---- */

		/**---- GET APPLICANT PROGRAM LIST START ---- */
		getWorkerApplicantProgramListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantProgramListRequest>
		) => {
			state.fetching = true;
			state.paramsListsDetailProgram = action?.payload;
		},
		getWorkerApplicantProgramListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantProgramListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerApplicantProgramList = content;
			state.totalProgram = total;
		},
		getWorkerApplicantProgramListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT PROGRAM LIST END ---- */

		/**---- GET APPLICANT PROCESS INFO START ---- */
		getWorkerApplicantProcessInfoListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantProcessInfoListRequest>
		) => {
			state.fetching = true;
			state.paramsListsDetailProcessInfo = action?.payload;
		},
		getWorkerApplicantProcessInfoListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantProcessInfoListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerApplicantProcessInfoList = content;
			state.totalProcessInfo = total;
		},
		getWorkerApplicantProcessInfoListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT PROCESS INFO END ---- */

		/**---- CREATE WORKER_APPLICANT START ---- */
		createWorkerApplicantRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		createWorkerApplicantSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER_APPLICANT END ---- */

		/**---- CREATE WORKER_APPLICANT PROGRAM START ---- */
		createWorkerApplicantProgramRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerApplicantProgramRequest>
		) => {
			state.fetching = true;
		},
		createWorkerApplicantProgramSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerApplicantProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER_APPLICANT PROGRAM END ---- */

		/**---- CREATE WORKER_APPLICANT LODGING START ---- */
		createWorkerApplicantLodgingRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerApplicantLodgingRequest>
		) => {
			state.fetching = true;
		},
		createWorkerApplicantLodgingSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerApplicantLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER_APPLICANT LODGING END ---- */

		/**---- UPDATE STATUS WORKER APPLICANT START ---- */
		updateStatusWorkerApplicantRequest: (
			state,
			action: PayloadAction<IPayloadUpdateStatusWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		updateStatusWorkerApplicantSuccess: (state) => {
			state.fetching = false;
		},
		updateStatusWorkerApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE STATUS WORKER APPLICANT END ---- */

		/**---- UPDATE EXTENSION PERIOD ---- */
		updateExtensionPeriodRequest: (
			state,
			action: PayloadAction<IPayloadUpdateExtensionPeriodRequest>
		) => {
			state.fetching = true;
		},
		updateExtensionPeriodSuccess: (state) => {
			state.fetching = false;
		},
		updateExtensionPeriodFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE EXTENSION PERIOD END ---- */

		/**---- DELETE WORKER_APPLICANT START ---- */
		deleteWorkerApplicantRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT END ---- */

		/**---- DELETE WORKER_APPLICANT ASSIGNMENT START ---- */
		deleteWorkerApplicantAssignmentRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantAssignmentSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantAssignmentFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT ASSIGNMENT END ---- */

		/**---- DELETE WORKER_APPLICANT ASSIGNMENT START ---- */
		deleteWorkerApplicantProgramRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantProgramSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT ASSIGNMENT END ---- */

		/**---- DELETE WORKER_APPLICANT LODGING START ---- */
		deleteWorkerApplicantLodgingRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantLodgingSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT LODGING END ---- */

		/**---- DELETE WORKER_APPLICANT PROCESSING INFO START ---- */
		deleteWorkerApplicantProcessingInfoRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantProcessingInfoSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantProcessingInfoFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT PROCESSING INFO END ---- */

		/**---- DELETE APPLICANT PROCESS INFO START ---- */
		deleteWorkerApplicantProcessInfoRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerApplicantProcessInfoRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerApplicantProcessInfoSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerApplicantProcessInfoFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE APPLICANT PROCESS INFO START ---- */

		/**---- GET APPLICANT DETAIL START ---- */
		getWorkerApplicantDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerApplicantDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailSuccess>
		) => {
			const { workerApplicantDetail } = action.payload;
			state.fetching = false;
			state.workerApplicantDetail = workerApplicantDetail;
		},
		getWorkerApplicantDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT DETAIL END ---- */

		/**---- GET APPLICANT RECOMMENDER START ---- */
		getWorkerApplicantRecommenderRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerApplicantRecommenderSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantRecommenderSuccess>
		) => {
			const { workerApplicantRecommender } = action.payload;
			state.fetching = false;
			state.workerApplicantRecommender = isNotNilOrEmpty(
				workerApplicantRecommender
			)
				? [workerApplicantRecommender]
				: [];
		},
		getWorkerApplicantRecommenderFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT RECOMMENDER END ---- */

		/**---- UPDATE APPLICANT RECOMMENDER START ---- */
		updateWorkerApplicantRecommenderRequest: (
			state,
			action: PayloadAction<IPayloadUpdateWorkerApplicantRecommenderRequest>
		) => {
			state.fetching = true;
		},
		updateWorkerApplicantRecommenderSuccess: (state) => {
			state.fetching = false;
		},
		updateWorkerApplicantRecommenderFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE APPLICANT RECOMMENDER END ---- */

		/**---- GET APPLICANT LODGING START ---- */
		getWorkerApplicantLodgingRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerApplicantLodgingSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantLodgingSuccess>
		) => {
			const { workerApplicantLodging } = action.payload;

			state.fetching = false;
			state.workerApplicantLodging = isNotNilOrEmpty(workerApplicantLodging)
				? [workerApplicantLodging]
				: [];
		},
		getWorkerApplicantLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT LODGING END ---- */

		/**---- GET APPLICANT LODGING AUTO FILL START ---- */
		getWorkerApplicantLodgingAutoFillRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerApplicantLodgingAutoFillSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantLodgingAutoFillSuccess>
		) => {
			const { workerApplicantLodgingAutoFill } = action.payload;
			state.fetching = false;
			state.workerApplicantLodgingAutoFill = workerApplicantLodgingAutoFill;
		},
		getWorkerApplicantLodgingAutoFillFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT LODGING AUTO FILL END ---- */

		/**---- UPDATE WORKER APPLICANT START ---- */
		updateWorkerApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IApplicantListItem>>
		) => {
			const { data } = action.payload;
			const workerApplicantList = [...state.workerApplicantList];
			state.workerApplicantList = formatWorkerApplicantList(
				mergeListItemUpdated<IWorkerApplicantListStore, IApplicantListItem>(
					workerApplicantList,
					data
				)
			);
		},
		updateWorkerApplicantDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TApplicantItem>>
		) => {
			const { data } = action.payload;
			const workerApplicantDetail = { ...state.workerApplicantDetail };
			state.workerApplicantDetail = formatWorkerApplicantDetail(
				mergeItemUpdated<IWorkerApplicantDetailStore, TApplicantItem>(
					workerApplicantDetail,
					data
				)
			);
		},
		/**---- UPDATE WORKER APPLICANT END ---- */

		/**---- UPLOAD EXCEL WORKER_APPLICANT START ---- */
		uploadExcelApplicantRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelApplicantRequest>
		) => {
			state.fetching = true;
			state.applicantUploadFailedList = [];
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelApplicantSuccess>
		) => {
			state.fetching = false;
			state.applicantUploadFailedList = action.payload.failedList;
			state.uploadExcelResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
		},
		uploadExcelApplicantFailure: (state) => {
			state.fetching = false;
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		/**---- UPLOAD EXCEL WORKER_APPLICANT END ---- */

		/**---- DELETE WORKER_APPLICANT START ---- */
		canAssignApplicantRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
		) => {
			state.fetching = true;
		},
		canAssignApplicantSuccess: (
			state,
			action: PayloadAction<IPayloadGetPublicFranchiseIdSuccess>
		) => {
			const { publicFranchiseId } = action.payload;
			state.fetching = false;
			state.publicFranchiseId = isNotNilOrEmpty(publicFranchiseId)
				? publicFranchiseId
				: null;
		},
		canAssignApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_APPLICANT END ---- */
		updateWorkerRecommenderApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TApplicantRecommender>>
		) => {
			const { data } = action.payload;
			const workerRecommenderApplicantList = [
				...state.workerApplicantRecommender,
			];
			state.workerApplicantRecommender = mergeListItemUpdated<
				IWorkerApplicantRecommenderStore,
				TApplicantRecommender
			>(workerRecommenderApplicantList, data);
		},
		updateWorkerLodgingApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TApplicantLodging>>
		) => {
			const { data } = action.payload;
			const workerLodgingApplicantList = [...state.workerApplicantLodging];
			state.workerApplicantLodging = mergeListItemUpdated<
				IWorkerApplicantLodgingStore,
				TApplicantLodging
			>(workerLodgingApplicantList, data);
		},

		getApplicantNameplateRequest: (
			state,
			action: PayloadAction<IPayloadApplicantCardRequest>
		) => {
			return {
				...state,
				nameplate: {} as IApplicantNameplate,
				nameplateFetching: true,
			};
		},
		getApplicantNameplateSuccess: (
			state,
			action: PayloadAction<IApplicantNameplate>
		) => {
			return {
				...state,
				nameplate: action.payload,
				nameplateFetching: false,
			};
		},
		getApplicantNameplateFailed: (state) => {
			return {
				...state,
				nameplate: {} as IApplicantNameplate,
				nameplateFetching: false,
			};
		},
		resetApplicantNameplate: (state) => {
			return {
				...state,
				nameplate: {} as IApplicantNameplate,
			};
		},
		resetWorkerApplicantDetail: (state) => {
			state.workerApplicantDetail = {} as IWorkerApplicantDetailStore;
		},
		resetWorkerApplicantRecommender: (state) => {
			state.workerApplicantRecommender =
				[] as IWorkerApplicantRecommenderStore[];
		},
		resetWorkerApplicantLodging: (state) => {
			state.workerApplicantLodging = [] as IWorkerApplicantLodgingStore[];
		},

		getWorkerApplicantDepartureDateRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDepartureDateRequest>
		) => {},
	},
});

export const WorkerApplicantSelectors = {
	selectFetching: (state: RootState) => state.workerApplicant.fetching,
	selectTotal: (state: RootState) => state.workerApplicant.total,
	selectTotalAssignment: (state: RootState) =>
		state.workerApplicant.totalAssignment,
	selectTotalProgram: (state: RootState) => state.workerApplicant.totalProgram,
	selectTotalProcessInfo: (state: RootState) =>
		state.workerApplicant.totalProcessInfo,
	selectParams: (state: RootState) => state.workerApplicant.params,
	selectParamsListsDetail: (state: RootState) =>
		state.workerApplicant.paramsListsDetail,
	selectParamsListsDetailAssignment: (state: RootState) =>
		state.workerApplicant.paramsListsDetailAssignment,
	selectParamsListsDetailProgram: (state: RootState) =>
		state.workerApplicant.paramsListsDetailProgram,
	selectParamsListsDetailProcessInfo: (state: RootState) =>
		state.workerApplicant.paramsListsDetailProcessInfo,
	selectList: (state: RootState) => state.workerApplicant.workerApplicantList,
	selectDetail: (state: RootState) =>
		state.workerApplicant.workerApplicantDetail,
	selectListApplicantAssignmentFetching: (state: RootState) =>
		state.workerApplicant.workerApplicantAssignmentFetching,
	selectListApplicantAssignment: (state: RootState) =>
		state.workerApplicant.workerApplicantAssignmentList,
	selectListApplicantProgram: (state: RootState) =>
		state.workerApplicant.workerApplicantProgramList,
	selectListApplicantProcessInfo: (state: RootState) =>
		state.workerApplicant.workerApplicantProcessInfoList,
	selectApplicantRecommender: (state: RootState) =>
		state.workerApplicant.workerApplicantRecommender,
	selectApplicantLodging: (state: RootState) =>
		state.workerApplicant.workerApplicantLodging,
	selectFailedList: (state: RootState) =>
		state.workerApplicant.applicantUploadFailedList,
	selectUploadExcelResult: (state: RootState) =>
		state.workerApplicant.uploadExcelResult,
	selectPublicFranchiseId: (state: RootState) =>
		state.workerApplicant.publicFranchiseId,
	selectApplicantNameplate: (state: RootState) =>
		state.workerApplicant.nameplate,
};

export const WorkerApplicantActions = workerApplicantSlice.actions;

export const WORKER_APPLICANT_PERSIST_KEY = 'workerApplicantPersist';
export const WorkerApplicantReducer = persistReducer(
	{
		key: WORKER_APPLICANT_PERSIST_KEY,
		storage: storage,
		whitelist: [
			'workerApplicantLodging',
			'workerApplicantRecommender',
			'workerApplicantAssignmentList',
		],
	},
	workerApplicantSlice.reducer
);
